import { AllConfig, DeviceConfig, DeviceTypes, PortTypeValues } from "../../config";
import { DisplayProcessingLogics, DisplayWidths, StatesToDisplayItem, PoolDeviceCriticalDesiredActual, PoolsideDaemonDeviceStateValues, StateMap } from "../Types";
import { IOnoffable, PoolDeviceInterface } from "../Interfaces";
import Locale from "../../utils/Locale";
import PoolDeviceOnOffable from "./base-onoffable";
import { ValueAndUnit } from "../../types";
import { State } from "../../status/Types";
import { AlertRecord } from "../../alert";


export enum criticalFieldIdentifiers {
    OnOff = "OnOff",
    OutputLevelPercent = "OutputLevelPercent",
}

export default class PoolDeviceThermistor extends PoolDeviceOnOffable implements PoolDeviceInterface, IOnoffable {
    static CanBeAddedToflow = true;
    static DeviceType = DeviceTypes.SALTCELL;
    static Icon = "salt-cell";
    OnOffIdentifier = criticalFieldIdentifiers.OnOff;
    static PickerTitle = Locale.gettext("Salt Cell");

    formatDeviceStatusCustom(DisplayProcessingLogic: DisplayProcessingLogics, value: any): ValueAndUnit {
        switch(DisplayProcessingLogic) {
        case DisplayProcessingLogics.RUNNINGIDLE:
            return { value: value === PoolsideDaemonDeviceStateValues.ON ?
                Locale.gettext("Running") :
                Locale.gettext("Idle")
            };
        default: return super.formatDeviceStatusCustom(DisplayProcessingLogic, value);
        }
    }

    getDisplayNameTranslated(displayName:string) {
        switch (displayName) {
        case "Flow Switch State": return Locale.gettext("Flow Switch State");
        case "Salt Level": return Locale.gettext("Salt Level");
        default: return super.getDisplayNameTranslated(displayName);
        }
    }

    getInformationFields(): StatesToDisplayItem[] {
        const isSerial = this.deviceConfig.HardwareDevice.Ports
            && this.deviceConfig.HardwareDevice.Ports[ 0 ]?.Type === PortTypeValues.Serial

        let order = 0;
        const fields = [];
    
        if (isSerial) {
            {
                const DisplayName = "Salt Level";
                const common = {
                    DisplayName, DisplayNameShort: DisplayName,
                    DisplayOrder: ++order,
                    DisplayProcessingLogic: DisplayProcessingLogics.INTEGER,
                    DisplayProcessingLogicUnitAsDisplayName: false,
                };
                fields.push(Object.assign({ Name: PoolsideDaemonDeviceStateNames.SaltLevel }, common));
            }
            {
                const common = {
                    DisplayName: "Flow Switch State",
                    DisplayNameShort: Locale.gettext("Flow"),
                    DisplayOrder: ++order,
                    DisplayProcessingLogic: DisplayProcessingLogics.ONOFF,
                    DisplayProcessingLogicUnitAsDisplayName: false,
                    DisplayWidth: DisplayWidths.WIDE
                };
                fields.push(Object.assign({ Name: PoolsideDaemonDeviceStateNames.FlowSwitchState }, common));
            }
            {
                const common = {
                    DisplayName: Locale.gettext("Volts"),
                    DisplayNameShort: Locale.gettext("Volts"),
                    DisplayOrder: ++order,
                    DisplayProcessingLogic: DisplayProcessingLogics.INTEGER,
                    DisplayProcessingLogicUnitAsDisplayName: false,
                };
                fields.push(Object.assign({ Name: PoolsideDaemonDeviceStateNames.Volts }, common));
            }
            {
                const DisplayName = Locale.gettext("Current");
                const common = {
                    DisplayName, DisplayNameShort: DisplayName,
                    DisplayOrder: ++order,
                    DisplayProcessingLogic: DisplayProcessingLogics.INTEGER,
                    DisplayProcessingLogicUnitAsDisplayName: false,
                };
                fields.push(Object.assign({ Name: PoolsideDaemonDeviceStateNames.Amps }, common));
            }
        } else {
            {
                const DisplayName = Locale.gettext("Duty Cycle");
                const common = {
                    DisplayName, DisplayNameShort: DisplayName,
                    DisplayOrder: ++order,
                    DisplayProcessingLogic: DisplayProcessingLogics.ONOFF,
                    DisplayProcessingLogicUnitAsDisplayName: false,
                };
                fields.push(Object.assign({ Name: PoolsideDaemonDeviceStateNames.DutyCyclePowerState }, common));
            }
            {
                const common = {
                    DisplayName: Locale.gettext("Duty Cycle Minutes"),
                    DisplayNameShort: Locale.gettext("Cycle Mins"),
                    DisplayOrder: ++order,
                    DisplayProcessingLogic: DisplayProcessingLogics.INTEGER,
                    DisplayProcessingLogicUnitAsDisplayName: false,
                };
                fields.push(Object.assign({ Name: PoolsideDaemonDeviceStateNames.DutyCycleMinutes }, common));
            }
        }
    
        return fields;
    }

    getCriticalFields(): PoolDeviceCriticalDesiredActual[] {
        let order = 0;
        const fields = [];
    
        {
            const DisplayName = Locale.gettext("Power");
            const common = {
                DisplayName, DisplayNameShort: DisplayName,
                DisplayOrder: ++order,
                DisplayProcessingLogic: DisplayProcessingLogics.RUNNINGIDLE,
                DisplayProcessingLogicUnitAsDisplayName: false,
            };
            fields.push({
                Identifier: criticalFieldIdentifiers.OnOff,
                // Does not have ActualPowerState
                ActualProperty: Object.assign({ Name: PoolsideDaemonDeviceStateNames.PowerState }, common),
                DesiredProperty: Object.assign({ Name: PoolsideDaemonDeviceStateNames.PowerState }, common)
            });
        }
        {
            const DisplayName = Locale.gettext("Output Level");
            const common = {
                DisplayName, DisplayNameShort: DisplayName,
                DisplayOrder: ++order,
                DisplayProcessingLogic: DisplayProcessingLogics.PERCENT,
                DisplayProcessingLogicUnitAsDisplayName: true,
            };
            fields.push({
                Identifier: criticalFieldIdentifiers.OutputLevelPercent,
                // Controller does not have ActualOutputLevelPercent prop.
                // At least at the moment we don't expect the Controller to publish an actual prop.
                // Because real Salt Cell pool devices dont provide this data.
                ActualProperty: Object.assign({ Name: PoolsideDaemonDeviceStateNames.DesiredOutputLevelPercent }, common),
                DesiredProperty: Object.assign({ Name: PoolsideDaemonDeviceStateNames.DesiredOutputLevelPercent }, common)
            });
        };
    
        return fields;
    }
}


export enum PoolsideDaemonDeviceStateNames {
    Amps = "Amps",
    DesiredOutputLevelPercent = "DesiredOutputLevelPercent",
    DutyCycleMinutes = "DutyCycleMinutes",
    DutyCyclePowerState = "DutyCyclePowerState",
    FlowSwitchState = "FlowSwitchState",
    PowerState = "PowerState",
    SaltLevel = "SaltLevel",
    Volts = "Volts",
}
